import React, { useEffect, useState } from "react";
import { brokersApi, firmApi } from "../../lib/api";
import { csvCell } from "../../lib/utils";
import { Card, AddToCampaignModal, useOutreach } from "../shared";
import { SendToFolkModal } from "../SendToFolkModal";
import { toast } from "sonner";
import { Search, Database, Upload, Trash2, Save, Mail, Sparkles, Download, UserSearch, Share2, Users } from "lucide-react";

const TYPES = ["Business Broker", "M&A Advisor", "Investment Bank", "Other"];
const STAGES = ["New", "Emailed", "On Buyer List", "Sending Deals", "Not A Fit"];
const S = (v) => String(v ?? "").trim();

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const delim = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
  const split = (l) => l.split(delim).map((c) => c.trim().replace(/^"|"$/g, ""));
  const hdr = split(lines[0]).map((h) => h.toLowerCase());
  const col = (...keys) => hdr.findIndex((h) => keys.some((k) => h.includes(k)));
  const i = { name: col("name", "contact", "advisor"), firm: col("firm", "company", "brokerage"), title: col("title", "role"),
    type: col("type", "category"), email: col("email"), phone: col("phone", "tel"), website: col("website", "url", "domain"),
    linkedin: col("linkedin"), city: col("city", "town"), state: col("state", "province"),
    specialty: col("specialty", "sector", "industry", "focus"), dealSize: col("deal size", "size", "range"), notes: col("note", "comment") };
  const first = hdr.findIndex((h) => h === "first name" || h === "first");
  const last = hdr.findIndex((h) => h === "last name" || h === "last");
  return lines.slice(1).map((l) => {
    const c = split(l);
    const g = (k) => (i[k] >= 0 ? S(c[i[k]]) : "");
    const name = first >= 0 ? `${S(c[first])} ${last >= 0 ? S(c[last]) : ""}`.trim() : g("name");
    return { name, firm: g("firm"), title: g("title"), type: g("type"), email: g("email").split(/[;,\s]/)[0], phone: g("phone"),
      website: g("website"), linkedin: g("linkedin"), city: g("city"), state: g("state"), specialty: g("specialty"),
      dealSize: g("dealSize"), notes: g("notes"), source: "upload" };
  }).filter((x) => x.name || x.firm);
};

// Find sell-side brokers / bankers by market, keep them in one list, and ask to join their buyer list.
export function BrokerFinder({ onEmail }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ state: "", city: "", specialty: "", type: "" });
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState("");
  const [firm, setFirm] = useState({});
  const [stats, setStats] = useState({ total: 0, byStage: {} });
  const [sel, setSel] = useState({});
  const [folkOpen, setFolkOpen] = useState(false);
  const outreach = useOutreach();
  const fileRef = React.useRef(null);
  const picked = rows.filter((r) => sel[r.id]);
  const withEmail = picked.filter((r) => r.email);
  const toContact = (b) => ({ id: b.id, name: b.name || b.firm, email: b.email, company: b.firm, phone: b.phone,
    title: b.title, website: b.website, type: b.type, specialty: b.specialty, dealSize: b.dealSize, stage: b.stage });

  const load = async () => {
    try {
      const [list, st] = await Promise.all([brokersApi.list({ q, limit: 300 }), brokersApi.stats()]);
      setRows(list);
      setStats(st);
    } catch { toast.error("Could not load the broker list"); }
  };
  useEffect(() => { load(); }, [q]); // eslint-disable-line
  useEffect(() => { firmApi.get().then((f) => setFirm(f || {})).catch(() => {}); }, []);

  const search = async (kind) => {
    if (!form.state && !form.city) return toast.error("Enter a state or city first");
    setBusy(kind);
    try {
      const r = kind === "apollo" ? await brokersApi.apollo(form) : await brokersApi.discover(form);
      setFound(r.brokers || []);
      toast[r.brokers?.length ? "success" : "warning"](r.brokers?.length ? `${r.brokers.length} found in ${r.location}` : `Nothing found in ${r.location}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
    finally { setBusy(""); }
  };

  const saveFound = async (only1) => {
    const batch = only1 ? [only1] : found;
    if (!batch.length) return;
    setBusy("save");
    try {
      const r = await brokersApi.bulk(batch);
      toast.success(`${r.added} added${r.updated ? `, ${r.updated} updated` : ""}`);
      if (only1) setFound((f) => f.filter((x) => x !== only1));
      else setFound([]);
      load();
    } catch { toast.error("Could not save"); }
    finally { setBusy(""); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const parsed = parseCsv(String(reader.result));
      if (!parsed.length) return toast.error("No rows found — needs a header row with Name / Firm / Email");
      try {
        const r = await brokersApi.bulk(parsed);
        toast.success(`${r.added} brokers added${r.updated ? `, ${r.updated} updated` : ""}`);
        load();
      } catch { toast.error("Upload failed"); }
    };
    reader.readAsText(f);
  };

  const setStage = async (b, stage) => {
    setRows((rs) => rs.map((r) => (r.id === b.id ? { ...r, stage } : r)));
    try { await brokersApi.update(b.id, { stage }); } catch { toast.error("Could not save the stage"); load(); }
  };
  const del = async (b) => {
    if (!window.confirm(`Remove ${b.name || b.firm}?`)) return;
    try { await brokersApi.remove(b.id); setRows((rs) => rs.filter((r) => r.id !== b.id)); } catch { toast.error("Delete failed"); }
  };

  const toggleAll = () => {
    const all = rows.length > 0 && rows.every((r) => sel[r.id]);
    setSel(all ? {} : Object.fromEntries(rows.map((r) => [r.id, true])));
  };

  const addToCampaign = () => {
    if (!withEmail.length) return toast.error("Selected brokers need an email — use “Find email” first");
    outreach.start(withEmail.map(toContact), "Broker buyer-list outreach", "brokers");
  };

  const enrich = async (b) => {
    setBusy(`en-${b.id}`);
    try {
      const r = await brokersApi.enrich(b.id);
      setRows((rs) => rs.map((x) => (x.id === b.id ? r : x)));
      toast.success(`Found ${r.email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Apollo could not find an email"); }
    finally { setBusy(""); }
  };

  const draft = async (b) => {
    setBusy(`mail-${b.id}`);
    try {
      const d = await brokersApi.buyerEmail({
        broker: b, firmName: firm.name || "XYZ Ventures",
        buyBox: [firm.sectors, firm.criteria].filter(Boolean).join(" · "),
        geography: firm.geography || "", checkSize: firm.checkSize || "", proof: firm.proofOfFunds || "",
      });
      onEmail?.({ ...b, subject: d.subject, body: d.body });
      toast.success("Drafted — review it in Compose");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft the email"); }
    finally { setBusy(""); }
  };

  const exportCsv = () => {
    const cols = ["name", "firm", "title", "type", "email", "phone", "city", "state", "specialty", "dealSize", "stage"];
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => csvCell(r[c])).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "brokers.csv";
    a.click();
  };


  return (
    <div data-testid="broker-finder">
      <Card title="Find M&A brokers & investment bankers" testid="bf-search">
        <div className="bf-form">
          <div className="field"><label>State</label>
            <input className="inp" placeholder="TX" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="bf-state" /></div>
          <div className="field"><label>City (optional)</label>
            <input className="inp" placeholder="Dallas" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="bf-city" /></div>
          <div className="field"><label>Specialty (optional)</label>
            <input className="inp" placeholder="outdoor power equipment dealers" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} data-testid="bf-specialty" /></div>
          <div className="field"><label>Type</label>
            <select className="inp" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="bf-type">
              <option value="">Any</option>{TYPES.map((t) => <option key={t}>{t}</option>)}
            </select></div>
        </div>
        <div className="bf-actions">
          <button className="btn btn-primary" onClick={() => search("apollo")} disabled={!!busy} data-testid="bf-apollo"><Database size={13} />{busy === "apollo" ? "Searching Apollo…" : "Apollo people search"}</button>
          <button className="btn" onClick={() => search("web")} disabled={!!busy} data-testid="bf-web"><Search size={13} />{busy === "web" ? "Scanning the web…" : "Find firms on the web"}</button>
          <input ref={fileRef} type="file" accept=".csv,.tsv,text/csv" style={{ display: "none" }} onChange={onFile} data-testid="bf-file" />
          <button className="btn" onClick={() => fileRef.current?.click()} data-testid="bf-upload"><Upload size={13} />Upload my broker list</button>
          <button className="btn" onClick={exportCsv} disabled={!rows.length} data-testid="bf-export"><Download size={13} />CSV</button>
        </div>
        <div className="bf-hint">Apollo returns named advisors with titles and emails. The web scan names the brokerage and banking firms covering that market — CSV columns: Name, Firm, Title, Type, Email, Phone, City, State, Specialty.</div>
      </Card>

      {found.length > 0 && (
        <Card title={`Search results — ${found.length}`} className="table-scroll" style={{ marginTop: 12 }} testid="bf-results"
          right={<button className="btn btn-primary" onClick={() => saveFound()} disabled={busy === "save"} data-testid="bf-save-all"><Save size={12} />Save all to my list</button>}>
          <table className="tbl" data-testid="bf-results-table">
            <thead><tr><th>Name</th><th>Firm</th><th>Type</th><th>Contact</th><th>Location</th><th>Specialty</th><th></th></tr></thead>
            <tbody>
              {found.map((b, i) => (
                <tr key={i} data-testid={`bf-result-${i}`}>
                  <td>{b.name || <span className="om-sub">—</span>}{b.title ? <div className="om-sub">{b.title}</div> : null}</td>
                  <td><b>{b.firm}</b>{b.website ? <div className="om-sub">{b.website.replace(/^https?:\/\/(www\.)?/, "")}</div> : null}</td>
                  <td style={{ fontSize: 11 }}>{b.type || "—"}</td>
                  <td style={{ fontSize: 11 }}>{b.email || <span className="om-sub">no public email</span>}{b.phone ? <div className="om-sub">{b.phone}</div> : null}</td>
                  <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{[b.city, b.state].filter(Boolean).join(", ")}</td>
                  <td style={{ fontSize: 11 }}>{(b.specialty || "").slice(0, 70)}</td>
                  <td><button className="btn btn-sm" onClick={() => saveFound(b)} data-testid={`bf-save-${i}`}><Save size={12} />Save</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title={`My brokers — ${stats.total || rows.length}`} className="table-scroll" style={{ marginTop: 12 }} testid="bf-list"
        right={<div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <input className="inp" style={{ width: 200 }} placeholder="Search name, firm, city…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bf-q" />
          <button className={`btn ${withEmail.length ? "btn-primary" : ""}`} onClick={addToCampaign} disabled={!picked.length} data-testid="bf-campaign"><Share2 size={13} />Add to campaign ({picked.length})</button>
          <button className="btn" onClick={() => (withEmail.length ? setFolkOpen(true) : toast.error("Selected brokers need an email — use “Find email” first"))} disabled={!picked.length} data-testid="bf-folk"><Users size={13} />Push to Folk ({picked.length})</button>
        </div>}>
        <div className="bf-counts">
          {STAGES.map((s) => <span key={s} className="chip" data-testid={`bf-count-${s.replace(/\s/g, "").toLowerCase()}`}>{s}: {(stats.byStage || {})[s] || 0}</span>)}
          {stats.total > rows.length && <span className="om-sub" data-testid="bf-truncated">showing {rows.length} of {stats.total} — search to narrow</span>}
        </div>
        <table className="tbl" data-testid="bf-table">
          <thead><tr>
            <th style={{ width: 28 }}><input type="checkbox" checked={rows.length > 0 && rows.every((r) => sel[r.id])} onChange={toggleAll} data-testid="bf-all" /></th>
            <th>Name</th><th>Firm</th><th>Type</th><th>Contact</th><th>Location</th><th>Specialty</th><th>Stage</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="om-empty" data-testid="bf-empty"><b>No brokers yet.</b> Search Apollo or the web above, or upload your own list.</td></tr>
            ) : rows.map((b) => (
              <tr key={b.id} data-testid={`bf-row-${b.id}`}>
                <td><input type="checkbox" checked={!!sel[b.id]} onChange={() => setSel((s) => ({ ...s, [b.id]: !s[b.id] }))} data-testid={`bf-check-${b.id}`} /></td>
                <td>{b.name || <span className="om-sub">—</span>}{b.title ? <div className="om-sub">{b.title}</div> : null}</td>
                <td><b>{b.firm}</b>{b.website ? <div className="om-sub">{b.website.replace(/^https?:\/\/(www\.)?/, "")}</div> : null}</td>
                <td style={{ fontSize: 11 }}>{b.type || "—"}</td>
                <td style={{ fontSize: 11 }}>{b.email || <span className="om-sub">—</span>}{b.phone ? <div className="om-sub">{b.phone}</div> : null}</td>
                <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{[b.city, b.state].filter(Boolean).join(", ")}</td>
                <td style={{ fontSize: 11 }}>{(b.specialty || "").slice(0, 70)}</td>
                <td>
                  <select className="inp" style={{ fontSize: 11 }} value={b.stage || "New"} onChange={(e) => setStage(b, e.target.value)} data-testid={`bf-stage-${b.id}`}>
                    {STAGES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {!b.email && <button className="btn btn-sm" onClick={() => enrich(b)} disabled={busy === `en-${b.id}`} title="Reveal the work email via Apollo" data-testid={`bf-enrich-${b.id}`}><UserSearch size={12} />{busy === `en-${b.id}` ? "…" : "Find email"}</button>}
                  <button className="btn btn-sm" onClick={() => onEmail?.(b)} disabled={!b.email} title={b.email ? "Open in Compose with the buyer-list template" : "No email on file"} data-testid={`bf-mail-${b.id}`}><Mail size={12} /></button>
                  <button className="btn btn-sm" onClick={() => draft(b)} disabled={busy === `mail-${b.id}`} title="Tailor the buyer-list email with AI" data-testid={`bf-ai-${b.id}`}><Sparkles size={12} />{busy === `mail-${b.id}` ? "…" : ""}</button>
                  <button className="btn btn-sm" onClick={() => del(b)} data-testid={`bf-del-${b.id}`}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts}
        defaultGroupName={outreach.defaultName} source={outreach.source} dealName="" />
      <SendToFolkModal open={folkOpen} onClose={() => setFolkOpen(false)} source="brokers" label="M&A Brokers"
        ids={withEmail.map((b) => b.id)} dealName="" />
    </div>
  );
}
